import { Button, Card, Form, Layout, Segmented, Spin, Typography } from "antd";
import { Content } from "antd/es/layout/layout";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAtom } from "jotai";
import { Navigate } from "react-router-dom";
import ComponentHeader from "./components/header";
import ComponentFooter from "./components/footer";
import { Defaults, QueryCase, QueryId, Section, ToastId, WebRoutes } from "../handlers/enums";
import { ComponentFormInput, ComponentFormPassword } from "./components/forms/input";
import { SectionSearch } from "./SectionSearch";
import { SectionSearchUserTbr } from "./SectionSearchUserTbr";
import { SectionSearchUserDone } from "./SectionSearchUserDone";
import { LastHomeSegment, Loading, UserId } from "../handlers/globals";
import { MutateData } from "../handlers/axios";
import { notifyWarning } from "../handlers/extensions";

export default function Home() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const { t } = useTranslation();

  const [userId, setUserId] = useAtom(UserId);
  const [segment, setSegment] = useAtom(LastHomeSegment);
  const [loading] = useAtom(Loading);

  const [isValidated, setIsValidated] = useState(Boolean(userId));
  const [username, setUsername] = useState(Defaults.StringEmpty);
  const [password, setPassword] = useState(Defaults.StringEmpty);
  const [freezeActions, setFreezeActions] = useState(false);

  const actionLogin = MutateData(QueryId.Login, () => {
    setFreezeActions(false);
  }, [
    {
      case: QueryCase.Success,
      onCase: (data) => {
        setUserId(data.data);
        setIsValidated(true);
      }
    },
    {
      case: QueryCase.Error,
      onCase: () => notifyWarning(ToastId.ActionWarning)
    }
  ]);

  const onLogin = () => {
    if (!username || !password) {
      notifyWarning(ToastId.RequiredFields);
      return;
    }

    setFreezeActions(true);
    actionLogin.mutate({
      Username: username,
      Password: password
    });
  };

  return (
    <>
      {loading ? (
        <div className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/45 backdrop-blur-sm">
          <Spin size="large" />
        </div>
      ) : null}
      <Layout className="!bg-transparent">
        <ComponentHeader />
        <Content className="relative min-h-[70vh]">
          {!isValidated ? (
            <Card className="mx-auto mt-2 max-w-xl border-white/10 bg-custom-grey/65 shadow-2xl">
              <Typography.Title level={3} className="!mb-1 !text-center !text-custom-white">
                {t("welcome")}
              </Typography.Title>
              <Typography.Paragraph className="!mb-6 !text-center !text-custom-white/70">
                {t("welcome_description")}
              </Typography.Paragraph>
              <Form layout="vertical" onFinish={onLogin}>
                <ComponentFormInput field="form_field_username" nolabel placeholder="placeholder_username" required onChange={(event) => setUsername(event.target.value)} />
                <ComponentFormPassword field="form_field_password" nolabel placeholder="placeholder_password" required onChange={(event) => setPassword(event.target.value)} />
                <Button htmlType="submit" className="mt-2 w-full font-semibold" disabled={freezeActions} loading={freezeActions}>
                  {t("login")}
                </Button>
              </Form>
            </Card>
          ) : (
            <Card className="mx-auto border-white/10 bg-custom-grey/65 shadow-2xl">
              <Segmented
                options={[
                  { label: t(Section.Search), value: Section.Search },
                  { label: t(Section.TBR), value: Section.TBR },
                  { label: t(Section.Done), value: Section.Done }
                ]}
                onChange={(value) => setSegment(value)}
                value={segment}
                className="mb-4 flex font-semibold"
                block
              />

              {segment === Section.Search ? (
                <SectionSearch />
              ) : segment === Section.TBR ? (
                <SectionSearchUserTbr mode={Section.TBR} />
              ) : segment === Section.Done ? (
                <SectionSearchUserDone mode={Section.Done} />
              ) : (
                <Navigate replace to={WebRoutes.Home} />
              )}
            </Card>
          )}
        </Content>
        <ComponentFooter />
      </Layout>
    </>
  );
}
